import {useState, useEffect, useRef} from 'preact/hooks'
import {FilterSidebar} from './components/FilterSidebar'
import {ResultsGrid} from './components/ResultsGrid'
import {SearchWidget} from './components/SearchWidget'
import {
    Travel,
    SearchFilters,
    FilterOptions,
    DropdownPayload,
    Termin,
    ExtIdItem,
    PrefillFiltersProps
} from './types/travel'
import './App.css'
import {FilterMobile} from "./components/FilterMobile.tsx";
import {trackEvent} from "./utils/analytics.ts";
import {toYearMonthDayTrackingString, truncateToMaxBytes} from "./utils/general.ts";

interface AppProps {
    viewType?: string
    contentInfusion?: string
    toolTipTexts?: string
    prefillFilters?: string
    kuratierteReisen?: string
    searchRedirects?: string
    pageSize?: number
}

// Environment-dependent API URL configuration
const getApiUrl = (): string => {
    // @ts-ignore - Vite's import.meta.env
    const mode = import.meta.env?.MODE || 'development'

    switch (mode) {
        case 'development':
            return 'http://weltweitwandern.at.localhost/api/meili/search.php'
        case 'demo':
            return 'https://wandern-demo.epsimec.dev/api/meili/search.php'
        case 'production':
            return 'https://www.weltweitwandern.at/api/meili/search.php'
        default:
            return 'http://weltweitwandern.at.localhost/api/meili/search.php'
    }
}

// Define valid filter keys
const validFilterKeys = [
    'destination', 'reiseart', 'interesse', 'anreiseart', 'travelType',
    'difficulty', 'price', 'duration', 'yearmonth', 'transport',
    'accommodation', 'locationChanges', 'recommendations', 'unterkunft',
    'rolle', 'veranstalter', 'standortwechsel', 'empfehlung', 'start', 'end'
]

// Function to parse query string into filters
const parseQueryStringToFilters = (prefillFilters: PrefillFiltersProps): SearchFilters => {

    const filters: SearchFilters = {}
    // Define valid filter keys from SearchFilters interface

    if(prefillFilters) {
        // Process prefill filters and set them to the filters object
        Object.entries(prefillFilters).forEach(([category, prefillItem]) => {
            if (prefillItem && prefillItem.selected && prefillItem.selected.length > 0) {
                // Map category names to valid filter keys if needed
                if (validFilterKeys.includes(category)) {
                    (filters as any)[category] = prefillItem.selected
                }
            }
        })

        if (Object.keys(filters).length > 0) {
            return filters
        }
    }

    const params = new URLSearchParams(window.location.search)

    validFilterKeys.forEach(key => {
        const value = params.get(key)
        if (value) {
            // Handle string fields (start, end) vs array fields
            if (key === 'start' || key === 'end') {
                (filters as any)[key] = value
            } else {
                // Split comma-separated values and filter out empty strings
                const values = value.split(',').map(v => v.trim()).filter(v => v.length > 0)
                if (values.length > 0) {
                    (filters as any)[key] = values
                }
            }
        }
    })

    return filters
}

// Function to update query string when filters change
const updateQueryStringFromFilters = (filters: SearchFilters) => {
    const params = new URLSearchParams(window.location.search)

    // Remove existing filter parameters
    validFilterKeys.forEach(key => {
        params.delete(key)
    })

    // Add current filter values
    Object.entries(filters).forEach(([key, value]) => {
        if (value && validFilterKeys.includes(key)) {
            if (Array.isArray(value) && value.length > 0) {
                params.set(key, value.join(','))
            } else if (typeof value === 'string' && value.trim() !== '') {
                params.set(key, value)
            }
        }
    })

    // Update URL without reloading the page
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`
    window.history.replaceState({}, '', newUrl)
}

const parseQueryStringToDateToggl = (): boolean => {
    const params = new URLSearchParams(window.location.search)
    const value = params.get('termine')
    return value !== 'true';
}

export function App(props: AppProps) {

    let prefillFiltersData = []
    if(props.prefillFilters) {
        prefillFiltersData = JSON.parse(props.prefillFilters);
    }

    const PAGE_SIZE = props.pageSize || 11

    const [filters, setFilters] = useState<SearchFilters>(props.viewType !== 'frontpage' ? parseQueryStringToFilters(prefillFiltersData) : {})
    const [results, setResults] = useState<Travel[]>([])
    const [termine, setTermine] = useState<Termin[]>([])
    const [extIdItems, setExtIdItems] = useState<ExtIdItem[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [isExtIdsLoaded, setIsExtIdsLoaded] = useState<boolean>(false)
    const [loadingMore, setLoadingMore] = useState<boolean>(false)
    const [isFilterOverlayOpen, setIsFilterOverlayOpen] = useState<boolean>(false)
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({})
    const [pagination, setPagination] = useState({ page: 1, terminPage: 1, limit: PAGE_SIZE, total: 0, terminTotal: 0})
    const [sortBy, setSortBy] = useState<string>('customsort')
    const [gridViewState, setGridViewState] = useState(parseQueryStringToDateToggl());
    const [dateTogglState, setDateTogglState] = useState(true);

    //tracking
    const isFirstLoad = useRef(true);
    const previousDestination = useRef<string[] | undefined>(undefined);
    const [searchCategory, ] = useState( 'reisekalender')
    /*const hasUrlParameters = (): boolean => {
        const params = new URLSearchParams(window.location.search)
        return validFilterKeys.some(key => params.has(key))
    }
    const [searchCategory, ] = useState( hasUrlParameters() ? 'redirect' : 'reisekalender');*/

    let contentInfusionData = [];
    if(props.contentInfusion) {
        contentInfusionData = JSON.parse(props.contentInfusion);
    }

    let toolTipTextsData  = [];
    if(props.toolTipTexts) {
        toolTipTextsData = JSON.parse(props.toolTipTexts);
    }

    let kuratierteReisenData = []
    if(props.kuratierteReisen && props.kuratierteReisen.length > 0 && props.viewType === 'frontpage') {
        kuratierteReisenData = JSON.parse(props.kuratierteReisen).kuratierte_reisen;
    }

    let searchRedirects = []
    if(props.searchRedirects) {
        searchRedirects = JSON.parse(props.searchRedirects);
    }

    // Fetch results and filter options from search API
    const fetchResultsAndFilters = async (
        page: number = 1,
        terminPage: number = 1,
        limit: number = PAGE_SIZE,
        appendResults: boolean = false,
        appendTerminResults: boolean = false,
        trackGAEvent: boolean = true
    ) => {
        try {
            if (appendResults) {
                setLoadingMore(true)
            } else {
                setLoading(true)
            }

            const response = await fetch(getApiUrl(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    filters: filters,
                    page: page,
                    terminPage: terminPage,
                    limit: limit,
                    sortBy: sortBy,
                    loadExtids: !isExtIdsLoaded
                })
            })

            const data = await response.json()

            if (data.error) {
                console.error('Search API error:', data.error)
                return
            }

            if(!isExtIdsLoaded && data.extids && Array.isArray(data.extids)) {
                setExtIdItems(data.extids)
                setIsExtIdsLoaded(true)
            }

            // Update results - append or replace based on mode
            if (appendResults) {
                setResults(prevResults => [...prevResults, ...(data.results || [])])
            } else {
                setResults(data.results || [])
            }

            if(appendTerminResults) {
                setTermine(prevResults => [...prevResults, ...(data.termine || [])])
            } else {
                setTermine(data.termine || [])
            }

            // Update pagination
            setPagination({
                page: data.page || 1,
                terminPage: data.terminPage || 1,
                limit: data.limit || PAGE_SIZE,
                total: data.total || 0,
                terminTotal: data.terminTotal || 0
            })

            // track event
           /* let allowTrackEvent = false
            let filtersDestinationLength = filters.destination?.length ?? 0;
            let filtersStartLength = filters.start?.length ?? 0;
            let filtersEndLength = filters.end?.length ?? 0;
            let filtersYearMonthLength = filters.yearmonth?.length ?? 0;
            let  isDestinationAndDatefilterSet = filtersDestinationLength > 0 &&
                (
                    (filtersStartLength > 0 && filtersEndLength > 0) ||
                    filtersYearMonthLength > 0
                );
            if(!trackGAEvent && searchCategory === 'reisekalender' && !isDestinationAndDatefilterSet) {
                allowTrackEvent = true
            } else {
                allowTrackEvent = trackGAEvent
            }

            if(allowTrackEvent) {*/

            if(trackGAEvent) {

                let trackDestinationData = filters.destination ?? [];
                let trackDestinationString = '';
                if(trackDestinationData.length > 0) {
                    trackDestinationString = trackDestinationData.join(',');
                }

                let trackSearchDates = 'egal';
                let trackDestinationYearMonth = filters.yearmonth ?? [];
                if(trackDestinationYearMonth.length > 0) {

                    trackSearchDates = trackDestinationYearMonth.map((item) => toYearMonthDayTrackingString(item))
                        .join(',');

                }

                let trackDestinationStart = filters.start ?? '';
                let trackDestinationEnd = filters.end ?? '';
                if(trackDestinationStart.length > 0 && trackDestinationEnd.length > 0) {
                    const trackingStart = toYearMonthDayTrackingString(trackDestinationStart);
                    const trackingEnd = toYearMonthDayTrackingString(trackDestinationEnd);
                    trackSearchDates = `${trackingStart} bis ${trackingEnd}`;
                }

                trackEvent('view_search_results', {
                    search_term: truncateToMaxBytes(trackDestinationString),
                    search_category: searchCategory,
                    search_datetime: truncateToMaxBytes(trackSearchDates),
                    search_results: data.total || 0
                })

            } else {
                console.log('no tracking')
            }

            // Update filter options from facet distribution
            if (data.facetDistribution) {
                setFilterOptions({
                    destination: data.facetDistribution.destination || [],
                    difficulty: data.facetDistribution.difficulty || [],
                    reiseart: data.facetDistribution.reiseart || [],
                    interesse: data.facetDistribution.interesse || [],
                    anreiseart: data.facetDistribution.anreiseart || [],
                    duration: data.facetDistribution.duration || [],
                    yearmonth: data.facetDistribution.yearmonth || [],
                    unterkunft: data.facetDistribution.unterkunft || [],
                    veranstalter: data.facetDistribution.veranstalter || [],
                    standortwechsel: data.facetDistribution.standortwechsel || [],
                    empfehlung: data.facetDistribution.empfehlung || [],
                    priceRange: data.priceRange || { min: 0, max: 5000 },
                    // Add other filter categories as they become available
                })
            }

        } catch (error) {
            console.error('Failed to fetch search results:', error)
            setResults([])
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }

    // Handle loading more results
    const handleLoadMore = () => {
        const nextPage = pagination.page + 1
        //auto reset termin page to 1
        fetchResultsAndFilters(nextPage, 1, pagination.limit, true, false, false)
    }

    const handleLoadMoreTermine = () => {
        const nextTerminPage = pagination.terminPage + 1
        //auto reset reise page to 1
        fetchResultsAndFilters(1, nextTerminPage, pagination.limit, false, true, false)
    }

    // Check if there are more results to load
    const hasMoreResults = results.length < pagination.total

    // Check if there are more results to load
    const hasMoreTerminResults = termine.length < pagination.terminTotal

    // Handle sort changes
    const handleSortChange = (payload: DropdownPayload) => {
        if (payload.value) {
            setSortBy(payload.value)
        }
    }

    //initial load of elements
    useEffect(() => {
        if(Object.keys(filters).length === 0) {
            fetchResultsAndFilters(1, 1, PAGE_SIZE, false, false, true)
            isFirstLoad.current = false
        }
    }, []);

    // Effect for filter changes - reset to page 1 and clear results
    useEffect(() => {
        // Skip initial render to avoid duplicate call with initial useEffect
        if (results.length > 0 || Object.keys(filters).length > 0) {

            const currentDestination = filters.destination;
            const hasDestinationChanged = JSON.stringify(previousDestination.current) !== JSON.stringify(currentDestination);
            const shouldTrack = hasDestinationChanged;
            previousDestination.current = currentDestination;

            fetchResultsAndFilters(1, 1, pagination.limit, false, false, shouldTrack)
            isFirstLoad.current = false
        }
    }, [filters, sortBy])

    // Effect to update query string when filters change
    useEffect(() => {
        if(props.viewType !== 'frontpage') {
            updateQueryStringFromFilters(filters)
        }
    }, [filters])


    return (
        <div className="app-container text-theme-black">
            {props.viewType === "frontpage" && (
                <main className="app-container-front">
                    <SearchWidget
                        filterOptions={filterOptions || {}}
                        onFiltersChange={setFilters}
                        filters={filters}
                        gridViewState={dateTogglState}
                        setGridViewState={setDateTogglState}
                        kuratierteReisen={kuratierteReisenData || []}
                        extIdItems={extIdItems}
                        searchRedirects={searchRedirects || []}
                    />
                </main>
            )}

            {props.viewType !== "frontpage" && (
                <main className="app-main">

                    <aside className="app-sidebar">
                        <FilterSidebar
                            gridViewState={dateTogglState}
                            setGridViewState={setDateTogglState}
                            filters={filters}
                            onFiltersChange={setFilters}
                            filterOptions={filterOptions || {}}
                            toolTipTexts={toolTipTextsData}
                            prefillFilters={prefillFiltersData}
                            kuratierteReisen={kuratierteReisenData || []}
                            extIdItems={extIdItems || []}
                            searchRedirects={searchRedirects || []}
                        />
                    </aside>

                    <div className="app-mobile-filter">
                        <FilterMobile
                            gridViewState={dateTogglState}
                            setGridViewState={setDateTogglState}
                            currentGridToggleViewState={gridViewState}
                            filters={filters}
                            onFiltersChange={setFilters}
                            onSortChange={handleSortChange}
                            total={pagination.total}
                            isFilterOverlayOpen={isFilterOverlayOpen}
                            setIsFilterOverlayOpen={setIsFilterOverlayOpen}
                            filterOptions={filterOptions || {}}
                            toolTipTexts={toolTipTextsData}
                            prefillFilters={prefillFiltersData}
                            extIdItems={extIdItems || []}
                        />
                    </div>

                    <section className="app-content">
                        <ResultsGrid
                            gridViewState={gridViewState}
                            setGridViewState={setGridViewState}
                            results={results}
                            termine={termine}
                            terminTotal={pagination.terminTotal}
                            loading={loading}
                            loadingMore={loadingMore}
                            filters={filters}
                            onSortChange={handleSortChange}
                            isFilterOverlayOpen={isFilterOverlayOpen}
                            setIsFilterOverlayOpen={setIsFilterOverlayOpen}
                            total={pagination.total}
                            limit={pagination.limit}
                            contentInfusion={contentInfusionData}
                            onLoadMore={handleLoadMore}
                            onLoadMoreTermine={handleLoadMoreTermine}
                            hasMoreResults={hasMoreResults}
                            hasMoreTerminResults={hasMoreTerminResults}
                            currentSortBy={sortBy}
                            toolTipTexts={toolTipTextsData}
                        />
                    </section>

                </main>
            )}
        </div>
    )
}
